import { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import {
  AppointmentDto,
  TimeSlotDto,
  LeaveOfAbsenceDto,
  EventTimeSlotDto,
  ContinuousEventTimeSlotDto,
  SublocalityDto,
  UserDto,
  PatientDto,
  WorkingHoursDto,
  WorkHourExceptionDtoTypeEnum,
  WorkHourExceptionDtoInitiatorEnum,
} from "../../../api-client";
import { useLocationId } from "../../../stores/User/User.hooks";
import {
  useAppointmentsForDateRange,
  useSublocalitiesForAppointmentsAddresses,
  useUsersByLocation,
  useDerivedWorkingHours,
  useDerivedPatientBlockedTimes,
} from "../../../stores/Calendar/calendar.queries";
import { calendarKeys } from "../../../stores/Calendar/calendar.keys";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { ClientTimeSlot } from "./adapters/types";
import { CalendarEvent, isCancelledAppointment } from "./adapters";
import { toCalendarEvents, toCalendarResources } from "./CalendarAdapters";
import notificationStore from "../../../stores/Notification/notificationStore";
import { updateReceivedAppointment } from "../../../helpers/calendar";
import { useSocketHandler } from "./SocketHandler";
import { EventReceiveArg } from "@fullcalendar/interaction";
import {
  useSaveAppointment,
  useUpdateTimeSlot,
  useDeleteLunchBreakForDay,
  useDeleteWorkHourException,
  useCreateWorkingHoursForDay,
  useDeleteGroupTherapist,
  useDeleteTimeSlot,
  useUserAbsence,
} from "../../../stores/Calendar/calendar.mutations";
import { useGuardedWorkHourExceptionMutations } from "../lunchBreak/useGuardedWorkHourExceptionMutations";
import {
  eventChangeToAppointmentUpdateData,
  eventChangeToTimeSlotDto,
  eventToClientLunchBreak,
} from "./adapters/display-to-client/reverse.adapter";
import { EventChangeArg } from "@fullcalendar/core";
import { useEventShelfController } from "../EventShelf/EventShelfController";
import { CalendarResource } from "./adapters/types";
import useCalendarValidations from "../Calendar.validations";
import horizonConfirmStore from "../../../stores/Calendar/horizonConfirmStore";
import { CalendarData, CalendarHandlers } from "./CalendarController";
import { useMultipleAppointmentNotes } from "../../../stores/Notes/notes.queries";
import { useMergeAppointmentGapsWarning } from "./shared/useMergeAppointmentGapsWarning";
import {
  isNoOpEventChange,
  correctEventDateTimeForSingleTherapistView,
  snapshotDateRangeCaches,
  restoreDateRangeCaches,
  projectEventsOntoDayColumns,
  sourceEventId,
} from "./shared/eventChangeHelpers";

// Stable empty singletons
const EMPTY_LOCKED_APPOINTMENTS: string[] = [];
const EMPTY_SUBLOCALITIES: SublocalityDto[] = [];

const DATE_FORMAT = "YYYY-MM-DD";
/** Initial half-width (per side) of the infinite-scroll window, in days, around the selected date. */
const WINDOW_INITIAL_DAYS = 7;
/** How many days to grow the window by each time the user scrolls to an edge. */
const WINDOW_EXTEND_DAYS = 7;

const getGroupParticipantAppointmentId = (participant: any): string | undefined => {
  const firstTimeSlot = participant?.timeSlots?.[0] || (participant?.type ? participant : undefined);
  return participant?.id || participant?.appointmentId || firstTimeSlot?.appointmentId;
};

/**
 * SingleTherapistCalendarController
 *
 * A controller for a weekly view of a single therapist's appointments.
 * Shows days from -7 to +7 around today for the selected therapist.
 * Uses GET /calendar/user/:userId/date-range endpoint.
 *
 * Shares common patterns with CalendarController but is scoped to one user and a date range.
 */
export const useSingleTherapistCalendarController = (): {
  data: CalendarData;
  handlers: CalendarHandlers;
} | null => {
  const locationId = useLocationId();
  const queryClient = useQueryClient();

  const CalendarStore = useStore(CalendarContext);
  const {
    selectedDateStr,
    isCancelledView,
    setCancelledView,
    selectedPatientId,
    setTargetPatientId,
    patientDrawerOpen,
    groupCheck,
    pinBoardItems,
    singleTherapistUserId,
    createTimeslot,
    createEventTimeSlot,
    createLeaveOfAbsence,
    focusPosition,
  } = CalendarStore;

  const { mutateAsync: deleteGroupTherapist } = useDeleteGroupTherapist();
  const { mutateAsync: deleteTimeslot } = useDeleteTimeSlot();
  const { mutateAsync: userAbsence } = useUserAbsence();

  // Visit-gap warning, shared with the main calendar controller. The dialog is
  // hosted shell-wide by CalendarActionDialogs.
  const { showMergeGapWarningIfPresent } = useMergeAppointmentGapsWarning();

  // WebSocket real-time updates
  useSocketHandler();

  const { handleAddToPinBoard, handleRemoveFromPinBoard } = useEventShelfController();
  const { isAppointmentOutsideWorkingHours, isPastHorizon, horizonDate } = useCalendarValidations();

  // Derived dates
  const selectedDate = useMemo(() => new Date(selectedDateStr), [selectedDateStr]);

  // Infinite-scroll window. Instead of a fixed ±7 day range, the visible window grows
  // as the user scrolls the day-columns: scrolling to the left edge prepends past days,
  // scrolling to the right edge appends future days. The window is re-centered on
  // selectedDateStr whenever the user navigates via the toolbar (prev/next or date picker).
  const [windowStart, setWindowStart] = useState(() =>
    moment(selectedDateStr).subtract(WINDOW_INITIAL_DAYS, "days").format(DATE_FORMAT)
  );
  const [windowEnd, setWindowEnd] = useState(() =>
    moment(selectedDateStr).add(WINDOW_INITIAL_DAYS, "days").format(DATE_FORMAT)
  );

  // Re-center the window when selectedDateStr changes externally (toolbar nav). Scroll-driven
  // extensions update windowStart/windowEnd without touching selectedDateStr, so they don't
  // trigger a re-center.
  const centeredDateRef = useRef(selectedDateStr);
  useEffect(() => {
    if (centeredDateRef.current === selectedDateStr) return;
    centeredDateRef.current = selectedDateStr;
    setWindowStart(
      moment(selectedDateStr).subtract(WINDOW_INITIAL_DAYS, "days").format(DATE_FORMAT)
    );
    setWindowEnd(moment(selectedDateStr).add(WINDOW_INITIAL_DAYS, "days").format(DATE_FORMAT));
  }, [selectedDateStr]);

  const startDate = windowStart;
  const endDate = windowEnd;

  const extendWindowBackward = useCallback(() => {
    setWindowStart((prev) =>
      moment(prev).subtract(WINDOW_EXTEND_DAYS, "days").format(DATE_FORMAT)
    );
  }, []);

  const extendWindowForward = useCallback(() => {
    setWindowEnd((prev) => moment(prev).add(WINDOW_EXTEND_DAYS, "days").format(DATE_FORMAT));
  }, []);

  // Fetch the selected user
  const { data: fetchedUsers = [] } = useUsersByLocation(
    locationId,
    singleTherapistUserId ? ({ userIds: [singleTherapistUserId] } as any) : undefined,
    false,
    !!locationId && !!singleTherapistUserId
  );

  const selectedUser = useMemo(
    () => fetchedUsers.find((u) => u.id === singleTherapistUserId),
    [fetchedUsers, singleTherapistUserId]
  );

  // Fetch appointments for the date range
  const {
    data: cachedAppointments,
    isLoading: appointmentsLoading,
  } = useAppointmentsForDateRange(
    singleTherapistUserId,
    startDate,
    endDate,
    !!singleTherapistUserId,
    true
  );

  const groupParticipantAppointmentIds = useMemo(() => {
    const appointmentIds = new Set<string>();
    cachedAppointments?.appointments?.forEach((appointment: any) => {
      appointment.participants?.forEach((participant: any) => {
        const participantAppointmentId = getGroupParticipantAppointmentId(participant);
        if (participantAppointmentId) {
          appointmentIds.add(participantAppointmentId);
        }
      });
    });
    return Array.from(appointmentIds);
  }, [cachedAppointments]);

  const { notesByAppointmentId } = useMultipleAppointmentNotes(groupParticipantAppointmentIds);

  // Fetch working hours for the date range
  const { data: derivedWorkingHours } = useDerivedWorkingHours(
    singleTherapistUserId ? [singleTherapistUserId] : undefined,
    startDate,
    endDate,
    !!singleTherapistUserId,
    locationId
  );

  // Fetch the target patient's derived non-availability (blocked times) for the date range.
  // Unifies recurring weekly patterns with specific dated blocked slots, resolved per day.
  const { data: patientBlockedTimes } = useDerivedPatientBlockedTimes(
    selectedPatientId,
    startDate,
    endDate,
    !!selectedPatientId
  );

  // Mutations
  const { mutateAsync: saveAppointment } = useSaveAppointment();
  const { mutate: updateTimeSlot } = useUpdateTimeSlot();
  const { createWorkHourException, updateWorkHourException } = useGuardedWorkHourExceptionMutations({
    promptOnOptionalBreak: false,
  });
  const { mutateAsync: deleteLunchBreakForDay } = useDeleteLunchBreakForDay();
  const { mutateAsync: deleteWorkHourException } = useDeleteWorkHourException();
  const createWorkingHoursForDay = useCreateWorkingHoursForDay();

  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: async ({ event }: { event: CalendarEvent }) => {
      // In single therapist view, resourceId is a date string, not a userId
      const actualDate = event.resourceId; // date column (e.g. "2026-05-22")
      const userId: string = singleTherapistUserId || "";
      const user = fetchedUsers.find((u: UserDto) => u.id === userId);

      // Correct start/end: use the actual date from the resource column + time from event
      const startTime = moment(event.start).format("HH:mm:ss");
      const endTime = moment(event.end).format("HH:mm:ss");
      const correctedStart = actualDate ? `${actualDate}T${startTime}` : moment(event.start!).toISOString();
      const correctedEnd = actualDate ? `${actualDate}T${endTime}` : moment(event.end!).toISOString();

      let result: any;

      switch (event.type) {
        case "LeaveOfAbsence": {
          if (!user?.id) throw new Error("User is required for LeaveOfAbsence");
          const leaveOfAbsence: LeaveOfAbsenceDto = {
            user,
            type: "LeaveOfAbsence",
            loAType: event.loaType,
            start: correctedStart,
            end: correctedEnd,
          };
          result = await userAbsence({ userId: user.id, leaveOfAbsence });
          break;
        }
        case "EventTimeSlot": {
          const eventTimeSlot: EventTimeSlotDto = {
            user,
            type: "EventTimeSlot",
            title: event.title,
            start: correctedStart,
            end: correctedEnd,
          };
          result = await createEventTimeSlot(eventTimeSlot);
          break;
        }
        default:
          break;
      }
      return result;
    },
    onSuccess: () => {
      invalidateCalendarQueries();
      notificationStore.showMessage("Event erfolgreich hinzugefügt", "success");
    },
    onError: (error) => {
      console.error("Create event failed", error);
      notificationStore.showMessage("Fehler beim Hinzufügen des Events", "error", error);
    },
  });

  // Refs for stable callbacks
  const fetchedUsersRef = useRef<UserDto[]>([]);
  const deleteTimeslotRef = useRef(deleteTimeslot);
  const deleteGroupTherapistRef = useRef(deleteGroupTherapist);
  const setTargetPatientIdRef = useRef(setTargetPatientId);
  const appointmentsRef = useRef<AppointmentDto[]>([]);
  const handleAddToPinBoardRef = useRef(handleAddToPinBoard);
  fetchedUsersRef.current = fetchedUsers;
  deleteTimeslotRef.current = deleteTimeslot;
  deleteGroupTherapistRef.current = deleteGroupTherapist;
  setTargetPatientIdRef.current = setTargetPatientId;
  appointmentsRef.current = cachedAppointments?.appointments || [];
  handleAddToPinBoardRef.current = handleAddToPinBoard;

  // Latest values for the memoized `onCreateEvent` callback (manual break creation, MD-227).
  const derivedWorkingHoursRef = useRef(derivedWorkingHours);
  const createWorkHourExceptionRef = useRef(createWorkHourException);
  const updateWorkHourExceptionRef = useRef(updateWorkHourException);
  derivedWorkingHoursRef.current = derivedWorkingHours;
  createWorkHourExceptionRef.current = createWorkHourException;
  updateWorkHourExceptionRef.current = updateWorkHourException;

  // Handlers
  const handleDateChange = useCallback((date: Date) => {
    // Date change handled by store
  }, []);

  const handleCancelledViewChange = useCallback(
    (checked: boolean) => setCancelledView(checked),
    [setCancelledView]
  );

  const createEventMutateAsyncRef = useRef(createEventMutation.mutateAsync);
  createEventMutateAsyncRef.current = createEventMutation.mutateAsync;

  const onCreateEvent = useCallback(
    async (event: CalendarEvent) => {
      try {
        if (event.type === "WorkingHours") {
          // In single therapist view, resourceId is a date string; use singleTherapistUserId
          const userId = singleTherapistUserId;
          const date = event.resourceId || moment(event.start).format(DATE_FORMAT);
          const startTime = moment(event.start).format("HH:mm");
          const endTime = moment(event.end).format("HH:mm");
          if (!userId || !date) throw new Error("Cannot create working hours: missing userId or date");
          await createWorkingHoursForDay.mutateAsync({ userId, date, startTime, endTime });
          return;
        }
        if (event.type === "LunchBreak") {
          // Manual break in the single-therapist view. resourceId is the date
          // column; event times are shifted to the display date, so rebuild
          // them onto the actual date. Send ONLY the break slot as an Override;
          // the backend composes the full day, enforces the minimum and deducts
          // the break from worked hours.
          const userId = singleTherapistUserId;
          const actualDate = event.resourceId || moment(event.start).format(DATE_FORMAT);
          if (!userId || !actualDate || !event.start || !event.end) {
            throw new Error("Cannot create break: missing user or time range");
          }
          const startIso = new Date(`${actualDate}T${moment(event.start).format("HH:mm:ss")}`).toISOString();
          const endIso = new Date(`${actualDate}T${moment(event.end).format("HH:mm:ss")}`).toISOString();
          const existingException = derivedWorkingHoursRef.current?.find(
            (wh) => wh.userId === userId && wh.date === actualDate && wh.source === "exception"
          );
          const breakSlot = { isBreak: true, start: startIso, end: endIso };
          if (existingException?.exceptionId) {
            await updateWorkHourExceptionRef.current({
              exceptionId: existingException.exceptionId,
              exception: {
                userId,
                date: actualDate,
                type: WorkHourExceptionDtoTypeEnum.Override,
                slots: [breakSlot],
                initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
              },
            });
          } else {
            await createWorkHourExceptionRef.current({
              exception: {
                userId,
                date: actualDate,
                type: WorkHourExceptionDtoTypeEnum.Override,
                slots: [breakSlot],
                initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
              },
            });
          }
          return;
        }

        // Manual break in the single-therapist view (MD-227). resourceId is
        // the date column; event times are shifted to the display date, so
        // rebuild them onto the actual date. Send ONLY the break slot as an
        // Override; the backend composes the full day, enforces the §4
        // minimum and validates the position.
        if (event.type === "LunchBreak") {
          const userId = singleTherapistUserId;
          const actualDate = event.resourceId || moment(event.start).format(DATE_FORMAT);
          if (!userId || !actualDate || !event.start || !event.end) {
            throw new Error("Cannot create break: missing user or time range");
          }
          const startIso = new Date(`${actualDate}T${moment(event.start).format("HH:mm:ss")}`).toISOString();
          const endIso = new Date(`${actualDate}T${moment(event.end).format("HH:mm:ss")}`).toISOString();
          const existingException = derivedWorkingHoursRef.current?.find(
            (wh) => wh.userId === userId && wh.date === actualDate && wh.source === "exception"
          );
          const breakSlot = { isBreak: true, start: startIso, end: endIso };
          if (existingException?.exceptionId) {
            await updateWorkHourExceptionRef.current({
              exceptionId: existingException.exceptionId,
              exception: {
                userId,
                date: actualDate,
                type: WorkHourExceptionDtoTypeEnum.Override,
                slots: [breakSlot],
                initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
              },
            });
          } else {
            await createWorkHourExceptionRef.current({
              exception: {
                userId,
                date: actualDate,
                type: WorkHourExceptionDtoTypeEnum.Override,
                slots: [breakSlot],
                initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
              },
            });
          }
          return;
        }

        // Horizon soft-block: creating an event time slot past the horizon needs
        // confirmation. In single-therapist view the real date is the column id.
        if (event.type === "EventTimeSlot") {
          const targetDate = event.resourceId || event.start;
          if (targetDate && isPastHorizon(targetDate)) {
            const proceed = await horizonConfirmStore.requestConfirm({
              date: targetDate,
              horizonDate,
              kind: "event",
            });
            if (!proceed) return;
          }
        }
        await createEventMutateAsyncRef.current({ event });
      } catch (error) {
        console.error("Event creation error", error);
      }
    },
    [createWorkingHoursForDay, isPastHorizon, horizonDate]
  );

  const onChangeEvent = useCallback(async (eventChangeInfo: EventChangeArg) => {
    // Skip reverts / no-op moves (e.g. FullCalendar's revert() after a failed
    // drag) so we don't re-issue an identical, potentially bouncing update.
    if (isNoOpEventChange(eventChangeInfo)) {
      return;
    }

    const event = eventChangeInfo.event;
    const timeSlotType = event.extendedProps?.timeSlotType;
    // In single therapist view, resourceId is a date, not a userId
    const actualUserId = event.extendedProps?.user?.id || singleTherapistUserId;
    // The resourceId is the actual date column (e.g. "2026-05-21")
    // Event start/end are shifted to selectedDateStr for display, so we need to restore the real date
    const actualDate = event.getResources()[0]?.id; // date string from column
    const { correctedStart, correctedEnd } =
      correctEventDateTimeForSingleTherapistView(event, actualDate);

    // Horizon soft-block: moving a treatment or event time slot past the
    // configured horizon requires confirmation. Single-therapist view is always
    // concrete (no continuous projection). LunchBreak/working-hours are exempt.
    {
      const isEventSlot = timeSlotType === "EventTimeSlot";
      const isWorkingOrOtherSlot =
        timeSlotType === "LunchBreak" ||
        timeSlotType === "TimeSlot" ||
        timeSlotType === "LeaveOfAbsence";
      const isTreatment = !isEventSlot && !isWorkingOrOtherSlot;
      if (correctedStart && (isEventSlot || isTreatment) && isPastHorizon(correctedStart)) {
        const proceed = await horizonConfirmStore.requestConfirm({
          date: correctedStart,
          horizonDate,
          kind: isEventSlot ? "event" : "appointment",
        });
        if (!proceed) {
          eventChangeInfo.revert();
          return;
        }
      }
    }

    // Optimistically update the appointmentsForDateRange cache to prevent bounce-back.
    // FullCalendar already shows the event at the new position; this keeps the React Query
    // cache in sync so re-renders don't revert the position while the mutation is in flight.
    const optimisticallyUpdateCache = () => {
      const appointmentId = event.extendedProps?.appointmentId || sourceEventId(event.id);
      const timeSlotId = event.extendedProps?.timeSlotId || sourceEventId(event.id);
      queryClient.setQueriesData(
        { queryKey: calendarKeys.appointmentsForDateRange.all() },
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            appointments: old.appointments?.map((appt: AppointmentDto) => {
              if (String(appt.id) === String(appointmentId)) {
                return {
                  ...appt,
                  start: correctedStart,
                  end: correctedEnd,
                  timeSlots: appt.timeSlots?.map((ts: any) => ({
                    ...ts,
                    start: correctedStart,
                    end: correctedEnd,
                  })),
                };
              }
              return appt;
            }),
            eventTimeSlots: old.eventTimeSlots?.map((ets: any) => {
              if (String(ets.id) === String(timeSlotId)) {
                return { ...ets, start: correctedStart, end: correctedEnd };
              }
              return ets;
            }),
            leaveOfAbsences: old.leaveOfAbsences?.map((loa: any) => {
              if (String(loa.id) === String(timeSlotId)) {
                return { ...loa, start: correctedStart, end: correctedEnd };
              }
              return loa;
            }),
          };
        }
      );
    };
    
    if (timeSlotType === "LunchBreak") {
      // Use eventToClientLunchBreak but correct the date (event times are shifted to selectedDateStr)
      const clientLunchBreak = eventToClientLunchBreak(event);
      // Correct the start/end to actual date
      const correctedLunchStart = new Date(`${actualDate}T${moment(clientLunchBreak.start).format("HH:mm:ss")}`);
      const correctedLunchEnd = new Date(`${actualDate}T${moment(clientLunchBreak.end).format("HH:mm:ss")}`);

      if (clientLunchBreak.isException) {
        updateWorkHourException({
          exceptionId: clientLunchBreak.exceptionId!,
          exception: {
            userId: actualUserId,
            date: actualDate,
            type: WorkHourExceptionDtoTypeEnum.Override,
            slots: [
              {
                slotId: clientLunchBreak.slotId,
                isBreak: true,
                start: correctedLunchStart.toISOString(),
                end: correctedLunchEnd.toISOString(),
              },
            ],
            initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
          },
        });
      } else {
        createWorkHourException({
          exception: {
            userId: actualUserId,
            date: actualDate,
            type: WorkHourExceptionDtoTypeEnum.Override,
            slots: [
              {
                isBreak: true,
                start: correctedLunchStart.toISOString(),
                end: correctedLunchEnd.toISOString(),
              },
            ],
            initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
          },
        });
      }
      return;
    }

    if (timeSlotType === "TimeSlot" || timeSlotType === "EventTimeSlot" || timeSlotType === "LeaveOfAbsence") {
      const snapshot = snapshotDateRangeCaches(queryClient);
      optimisticallyUpdateCache();
      updateTimeSlot(
        {
          id: sourceEventId(event.id),
          timeslot: {
            id: sourceEventId(event.id),
            start: correctedStart!,
            end: correctedEnd!,
            user: { id: actualUserId },
          },
        },
        {
          onError: (error) => {
            // Roll back the optimistic date-range patch so a failed move does
            // not leave the time slot stuck in its new (unsaved) position.
            restoreDateRangeCaches(queryClient, snapshot);
            queryClient.invalidateQueries({
              queryKey: calendarKeys.appointmentsForDateRange.all(),
            });
            notificationStore.showMessage(
              "Zeitfenster konnte nicht verschoben werden",
              "error",
              error,
            );
          },
        },
      );
    } else {
      const updateData = eventChangeToAppointmentUpdateData(eventChangeInfo);
      if (updateData) {
        // Override start/end with the corrected actual date
        updateData.start = correctedStart;
        updateData.end = correctedEnd;
        // Override user on all timeSlots with the actual therapist userId and fix times
        updateData.timeSlots?.forEach(ts => {
          ts.user = { id: actualUserId };
          ts.start = correctedStart!;
          ts.end = correctedEnd!;
        });
        const snapshot = snapshotDateRangeCaches(queryClient);
        optimisticallyUpdateCache();
        try {
          await saveAppointment({ id: updateData.id!, appointment: updateData });
        } catch (error) {
          // Roll back the optimistic date-range patch so a failed move does not
          // leave the appointment stuck in its new (unsaved) position.
          restoreDateRangeCaches(queryClient, snapshot);
          queryClient.invalidateQueries({
            queryKey: calendarKeys.appointmentsForDateRange.all(),
          });
          notificationStore.showMessage(
            "Termin konnte nicht verschoben werden",
            "error",
            error,
          );
        }
      }
    }
  }, [updateTimeSlot, saveAppointment, singleTherapistUserId, queryClient, isPastHorizon, horizonDate]);

  const onEventReceive = useCallback(
    async (eventReceive: EventReceiveArg) => {
      try {
        const event = eventReceive.event;
        // In single therapist view, resourceId is a date string
        const actualDate = event.getResources()[0]?.id; // e.g. "2026-05-22"
        const userId = singleTherapistUserId;

        const oldAppointment: AppointmentDto = {
          id: event.id!,
          timeSlots: event.extendedProps.timeSlots,
          frequency: event.extendedProps.frequency,
        };

        // Correct event start/end: displayed on selectedDateStr, restore actual date
        const startTime = moment(event.start).format("HH:mm:ss");
        const endTime = moment(event.end).format("HH:mm:ss");
        const correctedStart = actualDate
          ? new Date(`${actualDate}T${startTime}`)
          : event.start!;
        const correctedEnd = actualDate
          ? new Date(`${actualDate}T${endTime}`)
          : event.end!;

        // Horizon soft-block: scheduling a treatment past the horizon needs
        // explicit confirmation. Cancel discards the drop.
        if (isPastHorizon(correctedStart)) {
          const proceed = await horizonConfirmStore.requestConfirm({
            date: correctedStart,
            horizonDate,
            kind: "appointment",
          });
          if (!proceed) {
            event.remove();
            return;
          }
        }

        const newAppointment = updateReceivedAppointment(
          oldAppointment,
          userId!,
          correctedStart,
          correctedEnd
        );

        if (newAppointment) {
          const result: any = await saveAppointment({
            id: event.id!,
            appointment: newAppointment,
          });

          showMergeGapWarningIfPresent(result);

          handleRemoveFromPinBoard(event.id!);
        }
      } catch (error) {
        console.error("Error in onEventReceive:", error);
      }
    },
    [saveAppointment, singleTherapistUserId, handleRemoveFromPinBoard, selectedDateStr, showMergeGapWarningIfPresent, isPastHorizon, horizonDate]
  );

  const onRemoveEvent = useCallback(
    async (timeslotId: string, event?: CalendarEvent) => {
      try {
        // Lunch breaks are working-hours exceptions rather than timeslots, so they
        // can't be removed via deleteTimeslot. Mirror the main calendar and delegate
        // to the day-level lunch-break deletion. In single-therapist view the resource
        // column is the day (date string) and there is exactly one therapist.
        if (event?.extendedProps?.timeSlotType === "LunchBreak") {
          const userId = event.extendedProps?.user?.id || singleTherapistUserId;
          const date = event.resourceId || moment(event.start).format("YYYY-MM-DD");
          if (!userId || !date) {
            throw new Error("Cannot delete lunch break: missing userId or date");
          }
          // Per-break delete (MD-238 P3): only the selected break is removed.
          await deleteLunchBreakForDay({ userId, date, slotId: event.extendedProps?.slotId });
          return;
        }

        await deleteTimeslotRef.current(sourceEventId(timeslotId));
        invalidateCalendarQueries();
        notificationStore.showMessage("Event erfolgreich entfernt", "success");
      } catch (error) {
        console.error("Remove event failed", error);
        notificationStore.showMessage("Fehler beim Entfernen des Events", "error", error);
      }
    },
    [singleTherapistUserId, deleteLunchBreakForDay]
  );

  const onRemoveGroupTherapist = useCallback(
    async (timeslotId: string) => {
      await deleteGroupTherapistRef.current({ id: timeslotId });
      invalidateCalendarQueries();
    },
    []
  );

  const onRemoveContinuousEvent = useCallback(async (_timeslotId: string) => {
    // Not applicable in single therapist daily view
  }, []);

  const onMoveEventDate = useCallback(async (event: CalendarEvent) => {
    // Move an appointment from the calendar to the pin board (shelf).
    // In single therapist view, appointments live in the appointmentsForDateRange cache,
    // so we look them up locally instead of using the shared lookup hook.
    const ep: any = event.extendedProps || {};
    const tsType = ep.timeSlotType || ep.type;
    const isGroupPatient =
      tsType === "GroupTimeSlotPatient" || tsType === "ContinuousGroupTimeSlotPatient";

    if (isGroupPatient) {
      // The date-range endpoint embeds group participants as raw TimeSlotDto objects
      // inside the therapist appointment (no standalone patient appointment exists), and
      // the participant event's `appointmentId` resolves to the timeslot id. So we locate
      // the participant by its timeslot id, reconstruct a patient AppointmentDto for the
      // shelf, and remove the participant from the therapist appointment in the cache.
      const groupAppointmentId = ep.groupAppointmentId;
      const timeSlotId = sourceEventId(event.id) || ep.timeSlotId;
      if (!groupAppointmentId || !timeSlotId) return;

      const therapistApp = appointmentsRef.current.find((a) => a.id === groupAppointmentId);
      const participant: any = (therapistApp?.participants || []).find(
        (p: any) => p.id === timeSlotId || p.timeSlots?.some((ts: any) => ts.id === timeSlotId)
      );
      if (!participant) return;

      // Normalize to a raw timeslot regardless of wrapped/unwrapped shape.
      const participantTs: any = participant.timeSlots?.[0] || participant;
      const patientAppointmentId = participantTs.appointmentId || participant.id;

      const appDto: AppointmentDto = {
        ...(participant as any),
        id: patientAppointmentId,
        address: participant.address || therapistApp?.address,
        timeSlots: [{ ...participantTs }],
      };

      // Remove the participant from the therapist appointment so the chip disappears
      // from the calendar immediately.
      queryClient.setQueriesData(
        { queryKey: calendarKeys.appointmentsForDateRange.all() },
        (old: any) => {
          if (!old?.appointments) return old;
          return {
            ...old,
            appointments: old.appointments
              .map((a: any) => {
                if (a.id !== groupAppointmentId) return a;
                const participants = (a.participants || []).filter(
                  (p: any) =>
                    p.id !== timeSlotId && !p.timeSlots?.some((ts: any) => ts.id === timeSlotId)
                );
                // When the last participant is moved out, drop the now-empty
                // group-therapist umbrella so the empty group disappears from
                // the calendar
                if (participants.length === 0) return null;
                return { ...a, participants };
              })
              .filter(Boolean),
          };
        }
      );

      handleAddToPinBoardRef.current(appDto);
      notificationStore.showMessage("Termin ins Regal verschoben", "info");
      return;
    }

    const appointmentId = ep.appointmentId;
    if (!appointmentId) return;

    const appDto = appointmentsRef.current.find((a) => a.id === appointmentId);
    if (!appDto) return;

    appDto.timeSlots?.sort(
      (a: TimeSlotDto, b: TimeSlotDto) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    handleAddToPinBoardRef.current(appDto);
    notificationStore.showMessage("Termin ins Regal verschoben", "info");
  }, [queryClient]);

  const onAddShelfEvent = useCallback(async (_event: CalendarEvent) => {
    notificationStore.showMessage("Shelf-Events werden in der Einzeltherapeutenansicht noch nicht unterstützt", "info");
  }, []);

  const onPatientTabChange = useCallback(async (_newPatientId: string) => {}, []);
  const onTargetPatientChange = useCallback(async (newTarget: PatientDto) => {
    setTargetPatientIdRef.current(newTarget.id);
  }, []);
  const onSelectShelfAppointment = useCallback((_appId: string) => {}, []);
  const onDataRefreshFlag = useCallback(async () => {}, []);
  const onEventContentClick = useCallback(async (_event: ClientTimeSlot) => {}, []);

  const invalidateCalendarQueries = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: calendarKeys.appointmentsForDateRange.all(),
    });
  }, [queryClient]);

  const onOutdated = useCallback(() => {
    invalidateCalendarQueries();
  }, [invalidateCalendarQueries]);

  const onSelectedShelfEventReceive = useCallback(async (_appointment: AppointmentDto) => {}, []);

  // Filter appointments based on cancelled view
  const filteredAppointments = useMemo(() => {
    if (!cachedAppointments?.appointments) return [];
    const appointments = cachedAppointments.appointments;
    // Drop group-therapist umbrellas that have no participants left (e.g. the
    // last participant was moved out) so the empty group does not linger on the
    // calendar even after a backend refetch. Cancelled umbrellas are kept so the
    // "Stornierungen" view can show them.
    const isEmptyGroupUmbrella = (a: any) =>
      a.timeSlots?.some((ts: any) => ts.type === "GroupTimeSlotTherapist") &&
      !(a.participants?.length > 0) &&
      !isCancelledAppointment(a);
    const base = isCancelledView
      ? appointments.filter((a: any) => !isEmptyGroupUmbrella(a))
      : appointments.filter(
          (a: any) =>
            !isCancelledAppointment(a) &&
            !pinBoardItems.some((p) => p.id === a.id) &&
            !isEmptyGroupUmbrella(a)
        );
    // Slot-level pins ("Umziehen → Nur diese"): hide ONLY that slot, keep siblings visible.
    const pinnedSlotIds = new Set(
      pinBoardItems.filter((p: any) => p && p.isSlotPin).map((p: any) => p.id)
    );
    const withoutPinnedSlots = base
      .map((appointment: any) => {
        if (
          pinnedSlotIds.size === 0 ||
          !appointment.timeSlots?.some((ts: any) => pinnedSlotIds.has(ts.id))
        ) {
          return appointment;
        }
        const remainingSlots = appointment.timeSlots.filter(
          (ts: any) => !pinnedSlotIds.has(ts.id)
        );
        return remainingSlots.length > 0 ? { ...appointment, timeSlots: remainingSlots } : null;
      })
      .filter(Boolean);
    // The date-range endpoint returns group participants as raw TimeSlotDto objects
    // (patient under `participant.therapyRx.rx.patient`, no `timeSlots` array), whereas
    // the standard endpoint returns them already wrapped in a `timeSlots` array.
    // Normalize to the wrapped shape so EventContent and the adapters can read the
    // patient name from `participant.timeSlots[0].therapyRx.rx.patient` consistently.
    return withoutPinnedSlots.map((appointment: any) => {
      if (!appointment.participants?.length) return appointment;
      const normalizedParticipants = appointment.participants.map((p: any) => {
        const firstTimeSlot = p?.timeSlots?.[0] || (p?.type ? p : undefined);
        const participantAppointmentId = getGroupParticipantAppointmentId(p);
        const participantNotes = participantAppointmentId
          ? notesByAppointmentId[participantAppointmentId]
          : undefined;
        const existingHasNotes = Boolean(
          p?.hasNotes ||
            (p?.notes?.length || 0) > 0 ||
            Boolean(firstTimeSlot?.treatmentNote)
        );
        const normalizedParticipant = !p?.timeSlots && p?.type
          ? { ...p, id: participantAppointmentId || p.id, timeSlots: [{ ...p }] }
          : p;

        return {
          ...normalizedParticipant,
          notes: participantNotes ?? normalizedParticipant.notes,
          hasNotes:
            participantNotes !== undefined ? participantNotes.length > 0 : existingHasNotes,
        };
      });
      return { ...appointment, participants: normalizedParticipants };
    });
  }, [cachedAppointments, isCancelledView, notesByAppointmentId, pinBoardItems]);

  // Deconstruct appointment cache
  const { leaveOfAbsences, eventTimeSlots, travelTimes } = cachedAppointments || {
    appointments: [],
    leaveOfAbsences: [],
    eventTimeSlots: [],
    travelTimes: [],
  };

  // Fetch sublocalities
  const { data: sublocalities = EMPTY_SUBLOCALITIES } = useSublocalitiesForAppointmentsAddresses(
    filteredAppointments,
    !!locationId
  );

  const drawerWidth = 320;
  const headerWidth = patientDrawerOpen ? `calc(100% - ${drawerWidth}px)` : "100%";

  // Build resources - one column per day in the date range
  const resources: CalendarResource[] = useMemo(() => {
    if (!selectedUser || !startDate || !endDate) return [];

    const days: CalendarResource[] = [];
    const start = moment(startDate);
    const end = moment(endDate);

    while (start.isBefore(end)) {
      // Skip weekends
      if (start.day() !== 0 && start.day() !== 6) {
        const dateStr = start.format(DATE_FORMAT);
        const dayLabel = start.format("ddd DD.MM");

        // Get working hours for this specific day
        const dayWorkingHours = derivedWorkingHours?.filter(
          (wh) => wh.userId === singleTherapistUserId && !wh.isBreak && wh.date === dateStr
        ) || [];

        // Build businessHours for this day resource
        const businessHours = dayWorkingHours.map((wh) => ({
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6], // FullCalendar needs this for resource view
          startTime: moment(wh.start).format("HH:mm:ss"),
          endTime: moment(wh.end).format("HH:mm:ss"),
        }));

        // Count appointments and event timeslots outside working hours for this day
        let warningCount = 0;
        if (dayWorkingHours.length > 0) {
          const isOutsideWorkingHours = (startStr: string, endStr: string) => {
            const tsStartMin = moment(startStr).hours() * 60 + moment(startStr).minutes();
            const tsEndMin = moment(endStr).hours() * 60 + moment(endStr).minutes();
            return !dayWorkingHours.some((wh) => {
              const whStartMin = moment(wh.start).hours() * 60 + moment(wh.start).minutes();
              const whEndMin = moment(wh.end).hours() * 60 + moment(wh.end).minutes();
              return tsStartMin >= whStartMin && tsEndMin <= whEndMin;
            });
          };

          // Check appointment timeslots
          const dayAppts = filteredAppointments.filter((appt: any) => {
            const ts = appt.timeSlots?.[0];
            return ts && moment(ts.start).format(DATE_FORMAT) === dateStr;
          });
          dayAppts.forEach((appt: any) => {
            (appt.timeSlots || []).forEach((ts: any) => {
              if (!ts.start || !ts.end) return;
              if (isOutsideWorkingHours(ts.start, ts.end)) warningCount++;
            });
          });

          // Check event timeslots
          const dayEventSlots = (eventTimeSlots || []).filter(
            (ets: any) => ets.start && moment(ets.start).format(DATE_FORMAT) === dateStr
          );
          dayEventSlots.forEach((ets: any) => {
            if (!ets.start || !ets.end) return;
            if (isOutsideWorkingHours(ets.start, ets.end)) warningCount++;
          });
        }

        days.push({
          id: dateStr,
          title: dayLabel,
          businessHours: businessHours.length > 0 ? businessHours : undefined,
          extendedProps: {
            userId: singleTherapistUserId,
            warningCount: warningCount > 0 ? warningCount : undefined,
          },
        });
      }
      start.add(1, "day");
    }

    return days;
  }, [selectedUser, startDate, endDate, derivedWorkingHours, singleTherapistUserId, filteredAppointments]);

  // Convert lunch breaks from working hours
  const lunchBreakEvents = useMemo(() => {
    return derivedWorkingHours?.filter((wh) => wh.isBreak && wh.userId === singleTherapistUserId) || [];
  }, [derivedWorkingHours, singleTherapistUserId]);

  // Build calendar events
  const calendarEvents = useMemo(() => {
    const sourceEvents = toCalendarEvents({
      appointments: filteredAppointments || [],
      leaveOfAbsences: leaveOfAbsences || [],
      eventTimeslots: eventTimeSlots || [],
      continuousAppointments: [],
      continuousEventTimeSlots: [],
      lunchBreaks: lunchBreakEvents || [],
      publicHolidayWorkingHours: (derivedWorkingHours || []).filter(
        (wh) => wh.source === "public-holiday"
      ),
      continuousLunchBreaks: [],
      cancelledDays: [],
      travelTimes: travelTimes || [],
      patientBlockingSlots: [],
      groupCheck,
      lockedAppointments: EMPTY_LOCKED_APPOINTMENTS,
      isContinuous: false,
      sublocalities,
    });

    // Remap resourceId from userId to the date string of the event's start time
    // so events appear in the correct day column, shift start/end to
    // selectedDateStr (keeping the time) because FullCalendar's
    // resourceTimeGridDay only renders events for the current date, and split
    // events that cross midnight — multi-day leaves of absence above all — into
    // one block per day column.
    const events = projectEventsOntoDayColumns(sourceEvents, selectedDateStr, {
      start: startDate,
      end: endDate,
    });

    // Mark appointments outside working hours
    const workingHoursMap = new Map<string, WorkingHoursDto[]>();
    if (derivedWorkingHours) {
      derivedWorkingHours
        .filter((wh) => !wh.isBreak)
        .forEach((wh) => {
          const dateKey = wh.date || moment(wh.start).format(DATE_FORMAT);
          if (!workingHoursMap.has(dateKey)) workingHoursMap.set(dateKey, []);
          workingHoursMap.get(dateKey)!.push(wh);
        });
    }

    events.forEach((event) => {
      const eventType = event.extendedProps.eventType || event.extendedProps.timeSlotType;
      const excludedTypes = ["LeaveOfAbsence", "LunchBreak", "working-hours", "PublicHoliday"];
      const shouldCheck =
        eventType &&
        !excludedTypes.includes(eventType) &&
        event.resourceId &&
        event.start &&
        event.end;

      if (shouldCheck) {
        const dayWorkingHours = workingHoursMap.get(event.resourceId!) || [];
        // Use time-only comparison: events are shifted to selectedDateStr for display,
        // but working hours have the actual date — so only compare HH:mm
        const apptStartMin = new Date(event.start).getHours() * 60 + new Date(event.start).getMinutes();
        const apptEndMin = new Date(event.end).getHours() * 60 + new Date(event.end).getMinutes();
        const workBlocks = dayWorkingHours.filter(wh => !wh.isBreak);
        const isOutside = workBlocks.length > 0 && !workBlocks.some(block => {
          const blockStart = new Date(block.start);
          const blockEnd = new Date(block.end);
          const blockStartMin = blockStart.getHours() * 60 + blockStart.getMinutes();
          const blockEndMin = blockEnd.getHours() * 60 + blockEnd.getMinutes();
          return apptStartMin >= blockStartMin && apptEndMin <= blockEndMin;
        });
        event.extendedProps.isOutsideWorkingHours = isOutside;
      } else {
        event.extendedProps.isOutsideWorkingHours = false;
      }
    });

    // Patient non-availability (blocked times) rendered as background events.
    // Each block carries its own date (column) and is time-shifted onto selectedDateStr
    // so FullCalendar's resourceTimeGridDay renders it in the correct day column.
    (patientBlockedTimes || []).forEach((block, index) => {
      const startTime = moment(block.start).format("HH:mm:ss");
      const endTime = moment(block.end).format("HH:mm:ss");
      events.push({
        id: `patient-blocked-${block.patientId}-${block.date}-${index}`,
        title: "Patient nicht verfügbar",
        start: `${selectedDateStr}T${startTime}`,
        end: `${selectedDateStr}T${endTime}`,
        resourceId: block.date,
        editable: false,
        display: "background",
        backgroundColor: "rgba(120, 120, 120, 0.35)",
        classNames: ["patient-blocked-time"],
        extendedProps: {
          eventType: "availability",
          timeSlotType: "PatientSlot",
          isContinuous: false,
        },
      });
    });

    return events;
  }, [
    filteredAppointments,
    leaveOfAbsences,
    eventTimeSlots,
    travelTimes,
    lunchBreakEvents,
    groupCheck,
    sublocalities,
    derivedWorkingHours,
    isAppointmentOutsideWorkingHours,
    patientBlockedTimes,
    selectedDateStr,
    startDate,
    endDate,
  ]);

  // Assemble data
  const data: CalendarData = useMemo(
    () => ({
      appointments: filteredAppointments,
      leaveOfAbsences: leaveOfAbsences || [],
      eventTimeslots: eventTimeSlots || [],
      lunchBreaks: lunchBreakEvents,
      travelTimes: travelTimes || [],
      calendarEvents,
      selectedShelfAppointment: undefined,
      patientAvailabilities: [],
      patientUnavailableTypes: [],
      patient: undefined as any,
      patientAppointments: undefined,
      unscheduleds: [],
      shelfAppointments: [],
      shelfContinuousAppointments: [],
      isLoading: appointmentsLoading,
      selectedDate,
      selectedDateStr,
      isContinuousView: false,
      isCancelledView,
      patientDrawerOpen,
      selectedEventRxId: undefined,
      showOpenDrawerButton: false,
      lockedAppointments: EMPTY_LOCKED_APPOINTMENTS,
      sublocalities,
      headerWidth,
      users: fetchedUsers,
      resources,
      groupCheck,
      focusPosition,
    }),
    [
      filteredAppointments,
      leaveOfAbsences,
      eventTimeSlots,
      travelTimes,
      lunchBreakEvents,
      calendarEvents,
      appointmentsLoading,
      selectedDate,
      selectedDateStr,
      isCancelledView,
      patientDrawerOpen,
      headerWidth,
      fetchedUsers,
      resources,
      groupCheck,
      sublocalities,
      focusPosition,
    ]
  );

  const handlers: CalendarHandlers = useMemo(
    () => ({
      onDatesSet: handleDateChange,
      onContinuousViewChange: async () => {},
      onCancelledViewChange: handleCancelledViewChange,
      onCreateEvent,
      onChangeEvent,
      onEventReceive,
      onRemoveEvent,
      onRemoveContinuousEvent,
      onRemoveGroupTherapist,
      onMoveEventDate,
      onAddShelfEvent,
      onEventContentClick,
      onSelectedShelfEventReceive,
      onPatientTabChange,
      onTargetPatientChange,
      onCloseNotesDialog: () => {},
      onSelectShelfAppointment,
      onDataRefreshFlag,
      onOutdated,
      onReachWindowStart: extendWindowBackward,
      onReachWindowEnd: extendWindowForward,
      renderCalendar: invalidateCalendarQueries,
    }),
    [
      handleDateChange,
      handleCancelledViewChange,
      onCreateEvent,
      onChangeEvent,
      onEventReceive,
      onRemoveEvent,
      onRemoveContinuousEvent,
      onRemoveGroupTherapist,
      onMoveEventDate,
      onAddShelfEvent,
      onEventContentClick,
      onSelectedShelfEventReceive,
      onPatientTabChange,
      onTargetPatientChange,
      onSelectShelfAppointment,
      onDataRefreshFlag,
      onOutdated,
      extendWindowBackward,
      extendWindowForward,
      invalidateCalendarQueries,
    ]
  );

  return { data, handlers };
};
